import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useFlags } from "@stadium/services/launch-darkly";
import { displayStripeOriginalPaymentErrorMessageFlag } from "@stadium/services/launch-darkly/constants";
import { showErrorToast } from "@stadium/services/react-toastify";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MailToConcierge } from "@stadium/ui/src/components/mail-to";

import { formatOrderCheckoutErrors } from "@helpers/orders/checkout";
import { getPaymentMethods } from "@stadium/helpers/analytics";
import { jsonDataFormatter, jsonErrorFormatter } from "@stadium/helpers/formatter";

import { serializeStripeError, useStripe } from "@stadium/services/stripe";

import useAnalyticsActionEvents from "@hooks/use-analytics-action-events";

import { checkout, createPaymentIntent, updateOrderDetails } from "@actions/order";
import { getCartCountryId, getCartOrderId, getCartOrderNumber } from "@selectors/cart";

import { setStore } from "@slices/store";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { getCurrencies } from "@stadium/redux-utils/selectors/configSelector";
import useActionEvents from "@stadium/services/analytics/hooks/use-action-events";
import { useCurrentUserContext } from "@contexts/current-user-provider";

import { useStoreContext } from "@components/store/provider";
import { useOpenOrderContext } from "@contexts/open-order-context";
import { getIsPricelessShop, getStoreNumber } from "@selectors/store";

import { SM_PROGRAM_ID } from "@constants/app-defaults";
import { getConfirmationRoute } from "@constants/routes";
import {
  PAYMENT_CARD_INTENT_ERROR_TOAST_ID,
  PAYMENT_ERROR_TOAST_ID,
  STRIPE_ERROR_TOAST_ID,
} from "@constants/toast-ids";

const emptyObj = {};
const emptyArr = [];

const useOnCheckout = ({
  isCorporateBilled = false,
  creditCardId,
  useWallet,
  openUnavailableItemsModal,
  billingTaxNumber = "",
} = emptyObj) => {
  const [loading, setLoading] = useState();

  const dispatch = useDispatch();
  const router = useRouter();
  const stripe = useStripe();

  const { [displayStripeOriginalPaymentErrorMessageFlag]: showStripeOriginalErrorMessage } =
    useFlags();

  const number = useSelector(getCartOrderNumber);
  const orderId = useSelector(getCartOrderId);
  const { storeBasePath, parsedCookies: { affiliate } = emptyObj } = useStoreContext();
  const { openOrderId } = useOpenOrderContext();
  const currencies = useConfig(getCurrencies);
  const storeNumber = useSelector(getStoreNumber);
  const { trackShopRedemption } = useAnalyticsActionEvents();
  const { pushToDataLayer: pushPurchaseEventToDataLayer } = useActionEvents();
  const isPricelessShop = useSelector(getIsPricelessShop);
  const cartCountryId = useSelector(getCartCountryId);
  const { currentUserData = emptyObj } = useCurrentUserContext();
  const { email, firstName, lastName } = currentUserData;

  const redirectToConfirmation = () =>
    router.push(getConfirmationRoute({ storeBasePath, orderNumber: number }));

  const onSuccessCallback = ({ sub_total, ...eventObj }) => {
    dispatch(setStore({ isStoreOrderFetched: false }));
    trackShopRedemption(eventObj);
    redirectToConfirmation();
    pushPurchaseEventToDataLayer({
      eventName: `${eventObj.shop_id}_purchase`,
      eventProperties: {
        transaction_date: new Date().toISOString(),
        transaction_id: eventObj.transaction_id,
        purchase_total: eventObj.value,
        purchase_subtotal: sub_total,
        email,
        first_name: firstName,
        last_name: lastName,
      },
    });
  };

  const handlePaymentErrors = (errorResponse) => {
    if (errorResponse?.response?.data) {
      const errors = formatOrderCheckoutErrors(errorResponse.response.data);
      errors.forEach((error, index) =>
        showErrorToast({
          message: error,
          config: { toastId: `${PAYMENT_ERROR_TOAST_ID}-${index}` },
        }),
      );
    }
  };

  const handleCardIntentError = (er) => {
    const data = jsonDataFormatter(get(er, "response.data", emptyObj));
    const spreeLineItems = get(data, "spreeLineItems", emptyObj);
    const spreeProductSets = get(data, "spreeOrderProductSets", emptyObj);
    const spreeMedleyItemSets = get(data, "spreeMedleyItemSets", emptyObj);

    if (!isEmpty(spreeLineItems) || !isEmpty(spreeProductSets) || !isEmpty(spreeMedleyItemSets)) {
      openUnavailableItemsModal({
        unavailableLineItems: spreeLineItems,
        unavailableProductSets: spreeProductSets,
        unavailableMedleyItems: spreeMedleyItemSets,
      });
    } else {
      const paymentErrObj = jsonErrorFormatter(er, "response.payment")?.payment || emptyObj;
      const errMessage = Object.values(paymentErrObj)[0];
      if (errMessage)
        showErrorToast({
          message: errMessage,
          config: { toastId: PAYMENT_CARD_INTENT_ERROR_TOAST_ID },
        });
      else handlePaymentErrors(er);
    }
    setLoading(false);
  };

  const updateOrder = (data, callback = () => emptyObj, flushLoader = true) => {
    setLoading(true);
    dispatch(updateOrderDetails(orderId, { program_id: SM_PROGRAM_ID, ...data }))
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        setLoading(false);
        showErrorToast();
      })
      .finally(() => {
        flushLoader && setLoading(false);
      });
  };

  const createCardIntent = ({ creditCardId, orderTotal }, eventObj) => {
    setLoading(true);
    if (creditCardId && orderTotal) {
      const payload = {
        source: "cc",
        program_id: SM_PROGRAM_ID,
      };
      return createPaymentIntent(number, payload)
        .then(({ clientSecret }) => stripe.confirmCardPayment(clientSecret))
        .then((resp) => {
          const { error } = resp;
          if (error) {
            const { displayErrorMessageHtml } = serializeStripeError({
              error,
              MailTo: MailToConcierge,
              showOriginalMessage: showStripeOriginalErrorMessage,
            });
            showErrorToast({
              message: displayErrorMessageHtml,
              config: { toastId: STRIPE_ERROR_TOAST_ID },
            });
            setLoading(false);
            throw new Error(error);
          }
          return resp;
        })
        .then(() => placeFinalOrder(eventObj))
        .catch(handleCardIntentError);
    }
    return placeFinalOrder(eventObj);
  };

  const placeFinalOrder = (eventObj) => {
    const payload = {
      program_id: SM_PROGRAM_ID,
      payment_confirmation: "done",
      treat_checkout: !openOrderId,
      affiliate,
    };

    setLoading(true);
    dispatch(
      checkout(orderId, payload, {
        clearOrderRequired: false,
        successCb: () => onSuccessCallback(eventObj),
      }),
    ).catch((errorResponse) => {
      const data = jsonDataFormatter(get(errorResponse, "response.data", emptyObj));
      const spreeLineItems = get(data, "spreeLineItems", emptyObj);
      const spreeProductSets = get(data, "spreeOrderProductSets", emptyObj);
      if (!isEmpty(spreeLineItems) || !isEmpty(spreeProductSets)) {
        openUnavailableItemsModal({
          unavailableLineItems: spreeLineItems,
          unavailableProductSets: spreeProductSets,
        });
      } else handlePaymentErrors(errorResponse);
      setLoading(false);
    });
  };

  const onCheckout = ({
    useStoreSpecificPoints,
    useStadiumPoints,
    orderTotal,
    currencyCodeId,
    selectedAddress,
    subTotal,
  }) => {
    const currencyCode = currencies.find(({ id }) => id === currencyCodeId)?.code;

    const intentPayload = {
      creditCardId: creditCardId || "",
      orderTotal,
    };

    const paymentMethods = {
      credit_card: !!creditCardId,
      wallet_money: useWallet,
      store_specific_points: useStoreSpecificPoints,
      global_points: useStadiumPoints,
      corporate_billed: isCorporateBilled,
    };
    const fomattedPaymentMethod = getPaymentMethods(paymentMethods);

    const eventObj = {
      transaction_id: number,
      value: orderTotal,
      currency: currencyCode,
      shop_id: storeNumber,
      payment_methods: fomattedPaymentMethod,
      sub_total: subTotal,
    };

    if (isPricelessShop) {
      const pricelessShopPayload = {
        ship_address_id: selectedAddress?.externalId,
        tax_number: billingTaxNumber,
        ...(cartCountryId !== +selectedAddress.countryId && {
          countryId: selectedAddress.countryId,
        }),
      };
      updateOrder(
        pricelessShopPayload,
        () => {
          placeFinalOrder(eventObj);
        },
        false,
      );
    } else createCardIntent(intentPayload, eventObj);
  };

  return { loading, onCheckout, updateOrder, setLoading };
};

export default useOnCheckout;
